// DASHBOARD — the screen left of the compass (Joel, 2026-10-18). Home lands here.
// One glance, five cards, each a door to the screen that owns it:
//   NOW       the current Step              → its tool (Do it) / the orb
//   SHAPE     one dial per sector (Alignment Score, Aim orb) → that sector's overview
//   PANTHEON  acquired per floor             → SELF
//   NUMA      Charge · Load · Flow           → NUMA
//   TODAY     agenda, ‹ › Today/Week/Month   → METIS (today) / KRNOS (week, month)
// Nothing here is a new engine: every number is read from data the app already
// builds (useCurrentStep, useSelf, useAlignment, the FLO snapshot and the river
// model NUMA itself uses).
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { supabase } from '../lib/supabase';
import { getFloSnapshot, type FloSnapshot } from '../lib/flo';
import { FALLBACK_BASELINE_LOAD, type RiverModel } from '../lib/flo-weights';
import { loadRiverModel } from '../olmps/dayClose';
import { useCompassConfig } from '../olmps/sectorConfig';
import { wakeOf } from '../olmps/gameSettings';
import { Gauge, chargeGauge, flowGauge, loadGauge } from '../olmps/Gauge';
import type { OracleProject } from '../olmps/useOracleProjects';
import { computeComplete, completeLabel } from '../olmps/sectorProgress';
import { useKyrsTasks } from '../olmps/useKyrsTasks';
import { ALIGNMENT_STATUS, type AlignmentStatus } from '../olmps/alignment';
import { useAlignment } from '../olmps/useAlignment';
import type { UseTodayData } from '../olmps/useTodayData';
import { SECTORS, SECTOR_LABELS, type Sector } from '../domain/olmps';
import { GlassCard } from './ui';
import { ScreenHelp } from './ScreenHelp';
import { dashboardHelp } from '../olmps/help/dashboardHelp';
import { NAV_D } from './EdgeTabs';
import { useAgendaRange, type AgendaRange } from './useAgendaRange';
import { SEATS } from './roster';
import type { SelfState } from './useSelf';
import type { NowStep } from './useCurrentStep';
import { DISPLAY_FONT, SECTOR_COLOR, makeStyles, useV10 } from './theme';

/** A half-circle meter: track + filled share, reading in the middle. */
function ArcDial({ size, fraction, color, track, value, valueColor }: { size: number; fraction: number; color: string; track: string; value: string; valueColor: string }) {
  const sw = Math.max(5, size * 0.1);
  const r = size / 2 - sw / 2;
  const cy = r + sw / 2;
  const d = `M ${sw / 2} ${cy} A ${r} ${r} 0 0 1 ${size - sw / 2} ${cy}`;
  const len = Math.PI * r;
  const f = Math.max(0, Math.min(1, fraction));
  return (
    <View style={{ width: size, height: cy + sw / 2 }}>
      <Svg width={size} height={cy + sw / 2}>
        <Path d={d} stroke={track} strokeWidth={sw} strokeLinecap="round" fill="none" />
        {f > 0 ? <Path d={d} stroke={color} strokeWidth={sw} strokeLinecap="round" fill="none" strokeDasharray={`${f * len} ${len}`} /> : null}
      </Svg>
      <Text style={{ position: 'absolute', left: 0, right: 0, bottom: 0, textAlign: 'center', color: valueColor, fontSize: Math.max(12, size * 0.22), fontWeight: '600' }}>{value}</Text>
    </View>
  );
}

function greeting(now: Date): string {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

const fmtTime = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export interface DashboardViewProps {
  userId: string | undefined;
  today: UseTodayData;
  step: NowStep | null;
  projects: Partial<Record<Sector, OracleProject[]>>;
  self: SelfState;
  width: number;
  top: number;
  bottom: number;
  onDoStep: (s: NowStep) => void;
  onOpenNow: () => void;
  onOpenSector: (s: Sector) => void;
  onOpenPantheon: () => void;
  onOpenNuma: () => void;
  onOpenToday: () => void;
  onOpenCalendar: () => void;
}

export function DashboardView({ userId, today, step, projects, self, width, top, bottom, onDoStep, onOpenNow, onOpenSector, onOpenPantheon, onOpenNuma, onOpenToday, onOpenCalendar }: DashboardViewProps) {
  const t = useV10();
  const styles = useStyles();
  const { config } = useCompassConfig(userId);
  const game = config.game;
  const { board } = useAlignment(userId);

  // Complete % per sector (shared with the sector overview): done leaves / open leaves of the active pool.
  const seasonDays = game.alignmentWindows.endeavour;
  const kyrsTasks = useKyrsTasks(userId);
  const nowMs = today.now.getTime();
  const completeBy = useMemo(() => {
    const out = {} as Record<Sector, ReturnType<typeof computeComplete>>;
    for (const s of SECTORS) out[s] = computeComplete(s, projects, { now: new Date(nowMs), seasonDays, tasks: kyrsTasks });
    return out;
  }, [projects, nowMs, seasonDays, kyrsTasks]);

  // NUMA: the same readings NUMA shows — charge from the user's wake time, load on the river's scale.
  const [ownFlo, setOwnFlo] = useState<FloSnapshot | null>(null);
  const [river, setRiver] = useState<RiverModel | null>(null);
  const wakeKey = game.morningSessionTime;
  useEffect(() => {
    if (!userId) return;
    let live = true;
    getFloSnapshot(supabase, userId, 28, wakeOf(new Date(), game))
      .then((s) => live && setOwnFlo(s))
      .catch(() => undefined);
    loadRiverModel(supabase, userId)
      .then((m) => live && setRiver(m))
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, wakeKey, today.flo]);
  const flo = ownFlo ?? today.flo;
  const load = river ? river.totalLoad : flo?.load ?? 0;

  // Layout: the ▶ circle sits on the right edge, so the column keeps clear of it on that side
  // (phones give the left only a gutter; wide screens centre the column).
  const right = NAV_D + 12;
  const colW = Math.min(520, width - 14 - right);
  const padLeft = width - colW >= 2 * right ? (width - colW) / 2 : 14;
  const inner = colW - 28;
  const shapeSize = Math.min(64, Math.floor(inner / 5) - 6);
  const triSize = Math.min(96, Math.floor(inner / 3) - 10);

  const now = today.now;

  // TODAY card range: ‹ › steps Today → Week (next 7 days) → Month (next 30 days).
  const [range, setRange] = useState<AgendaRange>('day');
  const rangeEntries = useAgendaRange(userId, range, today.calendarEvents, now);
  const RANGES: AgendaRange[] = ['day', 'week', 'month'];
  const RANGE_LABEL: Record<AgendaRange, string> = { day: 'TODAY', week: 'WEEK', month: 'MONTH' };
  const ri = RANGES.indexOf(range);
  const RANGE_SHOWN = 6;
  const fmtDay = (d: Date) => d.toLocaleDateString([], { weekday: 'short', day: 'numeric' });

  // Alignment Score status → colour (Update Directive 18 Oct 2026 §7).
  const STATUS_COLOR: Record<AlignmentStatus, string> = {
    aligned: t.onPace,
    mostly: '#7fc48b',
    drifting: t.behind,
    misaligned: t.lapsed,
    neglected: t.lapsed,
    unset: t.ivoryFaint,
  };

  // Acquired counts only. No SHAPE deity can be acquired yet (their ATLAS action list is an open decision).
  const pantheon: { label: string; got: number | null; of: number }[] = [
    { label: 'SHAPE', got: 0, of: SECTORS.length },
    { label: 'ORDER', got: self.securedCount, of: SEATS.length },
    // PAN has no acquired state yet (the council is a live session, not a holding).
    { label: 'PAN', got: null, of: 0 },
  ];

  return (
    <ScrollView style={StyleSheet.absoluteFill} contentContainerStyle={{ paddingTop: top + 8, paddingBottom: bottom + 32, paddingLeft: padLeft }} showsVerticalScrollIndicator={false}>
      <View style={{ width: colW, gap: 12 }}>
        <View style={styles.hello}>
          <Text style={styles.helloTitle}>{greeting(now)}</Text>
          <Text style={styles.helloSub}>{now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}</Text>
          <ScreenHelp help={dashboardHelp} style={styles.help} />
        </View>

        {/* NOW */}
        <Pressable onPress={onOpenNow} accessibilityLabel="Open the current step">
          <GlassCard style={styles.card}>
            <Text style={styles.label}>NOW</Text>
            {step ? (
              <>
                <Text style={styles.stepTitle} numberOfLines={2}>{step.title}</Text>
                {step.trail.aim || step.trail.project ? (
                  <Text style={styles.sub} numberOfLines={1}>{step.trail.aim ? `Aim: ${step.trail.aim}` : step.trail.project}</Text>
                ) : null}
                <View style={styles.doRow}>
                  <Pressable onPress={() => onDoStep(step)} style={styles.doBtn} accessibilityLabel="Do this step">
                    <Text style={styles.doText}>Do it</Text>
                  </Pressable>
                  <Text style={styles.sub} numberOfLines={1}>{step.when}</Text>
                </View>
              </>
            ) : (
              <Text style={styles.sub}>No step waiting. Pick one on the compass.</Text>
            )}
          </GlassCard>
        </Pressable>

        {/* TODAY — ‹ › switches Today / Week / Month; tap opens METIS (today) or KRNOS. */}
        <Pressable onPress={range === 'day' ? onOpenToday : onOpenCalendar} accessibilityLabel={range === 'day' ? "Open today's agenda" : 'Open the calendar'}>
          <GlassCard style={styles.card}>
            <View style={styles.head}>
              <View style={styles.rangeNav}>
                <Pressable onPress={() => ri > 0 && setRange(RANGES[ri - 1])} disabled={ri === 0} hitSlop={10} accessibilityLabel="Previous range" style={styles.rangeBtn}>
                  <Text style={[styles.rangeArrow, ri === 0 && styles.rangeArrowOff]}>‹</Text>
                </Pressable>
                <Text style={[styles.label, styles.rangeLabel]}>{RANGE_LABEL[range]}</Text>
                <Pressable onPress={() => ri < RANGES.length - 1 && setRange(RANGES[ri + 1])} disabled={ri === RANGES.length - 1} hitSlop={10} accessibilityLabel="Next range" style={styles.rangeBtn}>
                  <Text style={[styles.rangeArrow, ri === RANGES.length - 1 && styles.rangeArrowOff]}>›</Text>
                </Pressable>
              </View>
              <Text style={styles.hint}>{range === 'day' ? 'METIS' : range === 'week' ? 'next 7 days · KRNOS' : 'next 30 days · KRNOS'}</Text>
            </View>
            {rangeEntries.length ? (
              <>
                {rangeEntries.slice(0, RANGE_SHOWN).map((e) => (
                  <View key={e.id} style={styles.agendaRow}>
                    {range !== 'day' ? <Text style={styles.agendaDay}>{fmtDay(e.day)}</Text> : null}
                    <Text style={styles.agendaTime}>{e.start ? fmtTime(e.start) : 'Anytime'}</Text>
                    <Text style={styles.agendaText} numberOfLines={1}>{e.title}</Text>
                  </View>
                ))}
                {rangeEntries.length > RANGE_SHOWN ? <Text style={styles.sub}>+{rangeEntries.length - RANGE_SHOWN} more</Text> : null}
              </>
            ) : (
              <Text style={styles.sub}>{range === 'day' ? 'Nothing else scheduled today.' : range === 'week' ? 'Nothing scheduled this week.' : 'Nothing scheduled this month.'}</Text>
            )}
          </GlassCard>
        </Pressable>

        {/* SHAPE */}
        <GlassCard style={styles.card}>
          <View style={styles.head}>
            <Text style={styles.label}>SHAPE</Text>
            <Text style={styles.hint}>alignment · last {game.alignmentWindows.aim} days</Text>
          </View>
          <View style={styles.row}>
            {SECTORS.map((s) => {
              const al = board[s].aim;
              const word = ALIGNMENT_STATUS[al.status].short;
              const color = STATUS_COLOR[al.status];
              const cpl = completeBy[s].complete;
              const pctText = cpl == null ? '—' : `${completeLabel(cpl)} complete`;
              return (
                <Pressable key={s} onPress={() => onOpenSector(s)} style={styles.cell} accessibilityLabel={`${SECTOR_LABELS[s]}: alignment ${al.shown == null ? 'not set up' : `${al.shown} of 4`}, ${ALIGNMENT_STATUS[al.status].word}, ${cpl == null ? 'no completion yet' : `${completeLabel(cpl)} complete`}`}>
                  <ArcDial size={shapeSize} fraction={al.raw == null ? 0 : al.raw / 4} color={color} track={t.track} value={al.shown == null ? '–' : String(al.shown)} valueColor={t.ivory} />
                  <Text style={[styles.cellName, { color: SECTOR_COLOR[s] }]}>{s}</Text>
                  <Text style={[styles.cellStatus, { color }]} numberOfLines={1}>{word}</Text>
                  <Text style={styles.cellPct} numberOfLines={1}>{pctText}</Text>
                </Pressable>
              );
            })}
          </View>
        </GlassCard>

        {/* PANTHEON */}
        <Pressable onPress={onOpenPantheon} accessibilityLabel="Open the Pantheon">
          <GlassCard style={styles.card}>
            <View style={styles.head}>
              <Text style={styles.label}>PANTHEON</Text>
              <Text style={styles.chev}>›</Text>
            </View>
            <View style={styles.row}>
              {pantheon.map((p) => (
                <View key={p.label} style={styles.cell}>
                  <ArcDial size={triSize} fraction={p.got == null || !p.of ? 0 : p.got / p.of} color={t.gold} track={t.track} value={p.got == null ? '–' : `${p.got}/${p.of}`} valueColor={t.ivory} />
                  <Text style={styles.cellName}>{p.label}</Text>
                </View>
              ))}
            </View>
          </GlassCard>
        </Pressable>

        {/* NUMA */}
        <Pressable onPress={onOpenNuma} accessibilityLabel="Open NUMA">
          <GlassCard style={styles.card}>
            <View style={styles.head}>
              <Text style={styles.label}>NUMA</Text>
              <Text style={styles.chev}>›</Text>
            </View>
            {flo ? (
              <View style={styles.row} pointerEvents="none">
                <Gauge label="FLOW" value={String(Math.round(flo.flow.score))} size={triSize} {...flowGauge(t, flo.flow.score, game.greenBands)} />
                <Gauge label="LOAD" value={String(Math.round(load))} size={triSize} {...loadGauge(t, load, game.greenBands, river?.baseline ?? FALLBACK_BASELINE_LOAD)} />
                <Gauge label="CHARGE" value={flo.charge.toFixed(1)} size={triSize} {...chargeGauge(t, flo.charge, game.greenBands)} />
              </View>
            ) : (
              <Text style={styles.sub}>{today.floError || 'Loading…'}</Text>
            )}
          </GlassCard>
        </Pressable>

      </View>
    </ScrollView>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    hello: { paddingHorizontal: 4, paddingTop: 4, paddingBottom: 2 },
    helloTitle: { color: t.ivory, fontSize: 26, fontFamily: DISPLAY_FONT },
    helloSub: { color: t.ivorySoft, fontSize: 13, marginTop: 2 },
    help: { position: 'absolute', right: 4, top: 8 },
    card: { paddingHorizontal: 14, paddingVertical: 12 },
    head: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    label: { color: t.goldBright, fontSize: 11, fontWeight: '600', letterSpacing: 1.4 },
    hint: { color: t.ivoryFaint, fontSize: 11 },
    chev: { color: t.ivorySoft, fontSize: 20, lineHeight: 20 },
    stepTitle: { color: t.ivory, fontSize: 18, fontWeight: '600', marginTop: 6 },
    sub: { color: t.ivorySoft, fontSize: 12, marginTop: 3, flexShrink: 1 },
    doRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 10 },
    doBtn: { backgroundColor: t.gold, borderRadius: 18, paddingHorizontal: 18, paddingVertical: 7 },
    doText: { color: t.onGold, fontSize: 13, fontWeight: '700' },
    row: { flexDirection: 'row', justifyContent: 'space-around', alignItems: 'flex-start', marginTop: 8 },
    cell: { alignItems: 'center', flex: 1 },
    cellName: { color: t.ivory, fontSize: 12, fontWeight: '600', marginTop: 4 },
    cellStatus: { fontSize: 10, marginTop: 1 },
    cellPct: { color: t.ivoryFaint, fontSize: 9, marginTop: 1 },
    agendaRow: { flexDirection: 'row', gap: 12, paddingVertical: 5, marginTop: 2 },
    agendaTime: { color: t.ivorySoft, fontSize: 13, width: 64 },
    agendaDay: { color: t.goldBright, fontSize: 13, width: 54 },
    rangeNav: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    rangeBtn: { paddingHorizontal: 4 },
    rangeArrow: { color: t.ivory, fontSize: 20, lineHeight: 20 },
    rangeArrowOff: { color: t.ivoryFaint },
    rangeLabel: { minWidth: 52, textAlign: 'center' },
    agendaText: { color: t.ivory, fontSize: 13, flex: 1 },
  }),
);
